import { useState } from 'react';

import {
  FreshStrawFrozenEmbryo,
  useGetFreshStrawFrozenEmbryosByShipmentQuery,
  useGetFreshStrawFrozenEmbryosQuery,
} from '@/app/services/freshStrawRest';

import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';
import { FreshStrawEmbryoEditDialog } from '../FreshStrawEmbryoEditDialog';
import { FreshStrawImplantFrozenDialog } from '../FreshStrawImplantFrozenDialog';
import { FreshStrawPartnershipDialog } from '../FreshStrawPartnershipDialog';

interface FreshStrawFrozenTabProps {
  session: FreshStrawSession;
}

export function FreshStrawFrozenTab({ session }: FreshStrawFrozenTabProps) {
  const [storageLocationId, setStorageLocationId] = useState('');
  const [caneNumber, setCaneNumber] = useState('');
  const [partnershipEmbryo, setPartnershipEmbryo] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [editEmbryo, setEditEmbryo] = useState<FreshStrawFrozenEmbryo | null>(
    null,
  );
  const [implantEmbryo, setImplantEmbryo] =
    useState<FreshStrawFrozenEmbryo | null>(null);

  const farmQuery = useGetFreshStrawFrozenEmbryosByShipmentQuery(
    { shipmentId: session.freshShipmentId },
    { skip: !session.isTripScoped || !session.freshShipmentId },
  );

  const labQuery = useGetFreshStrawFrozenEmbryosQuery(
    {
      transferDate: session.transferDate,
      transferSiteId: session.transferSite,
      accountId: session.accountId || undefined,
      freshShipmentId: session.freshShipmentId || undefined,
      storageLocationId: storageLocationId || undefined,
      caneNumber: caneNumber || undefined,
    },
    {
      skip:
        session.isTripScoped ||
        !session.transferSite ||
        (!storageLocationId && !caneNumber),
    },
  );

  const query = session.isTripScoped ? farmQuery : labQuery;
  const rows = (query.data ?? []) as FreshStrawFrozenEmbryo[];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {!session.isTripScoped && (
        <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Storage Location ID
            </label>
            <input
              type="text"
              value={storageLocationId}
              onChange={(e) => setStorageLocationId(e.target.value)}
              placeholder="Guid"
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Cane Number
            </label>
            <input
              type="text"
              value={caneNumber}
              onChange={(e) => setCaneNumber(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
          <div className="text-xs text-gray-500">
            Set at least one of the above to load frozen embryos.
          </div>
        </div>
      )}

      {session.isTripScoped && !session.freshShipmentId && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Select a fresh shipment in the filter panel above to load frozen
          embryos for this trip.
        </div>
      )}

      {query.isFetching && (
        <div className="text-sm text-gray-500">Loading…</div>
      )}

      {query.isError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to load frozen embryos.
        </div>
      )}

      {!query.isFetching && rows.length === 0 && (
        <div className="flex flex-1 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
          No frozen embryos match the current filters.
        </div>
      )}

      <FreshStrawPartnershipDialog
        open={partnershipEmbryo !== null}
        onOpenChange={(open) => {
          if (!open) setPartnershipEmbryo(null);
        }}
        embryoId={partnershipEmbryo?.id ?? null}
        embryoLabel={partnershipEmbryo?.label}
      />

      <FreshStrawImplantFrozenDialog
        open={implantEmbryo !== null}
        onOpenChange={(open) => {
          if (!open) setImplantEmbryo(null);
        }}
        embryoId={implantEmbryo?.embryoId ?? null}
        embryoLabel={
          implantEmbryo?.mating ??
          implantEmbryo?.cane ??
          implantEmbryo?.embryoId
        }
        transferDate={session.transferDate}
        transferSiteId={session.transferSite}
        defaultFarmLocationId={session.locationId || undefined}
        onImplanted={() => setImplantEmbryo(null)}
      />

      <FreshStrawEmbryoEditDialog
        open={editEmbryo !== null}
        onOpenChange={(open) => {
          if (!open) setEditEmbryo(null);
        }}
        embryoId={editEmbryo?.embryoId ?? null}
        initial={
          editEmbryo
            ? {
                mating: editEmbryo.mating ?? '',
                clientRecipient: editEmbryo.recipName ?? '',
                cane: editEmbryo.cane ?? '',
                strawNumber: editEmbryo.strawNumber ?? '',
                embTagNumber: editEmbryo.tagNumber ?? '',
                stage: editEmbryo.stage?.toString() ?? '',
                grade: editEmbryo.grade?.toString() ?? '',
                quality: editEmbryo.quality?.toString() ?? '',
                distance: editEmbryo.distance?.toString() ?? '',
                rightLeft: editEmbryo.clLeftRight ?? '',
                comment: editEmbryo.implantComment ?? '',
              }
            : undefined
        }
      />

      {rows.length > 0 && (
        <div className="min-h-0 flex-1 overflow-auto rounded border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-100 text-left text-xs font-semibold text-gray-600">
              <tr>
                <th className="px-2 py-1">Mating</th>
                <th className="px-2 py-1">Ranch</th>
                <th className="px-2 py-1">Recip</th>
                <th className="px-2 py-1">Cane</th>
                <th className="px-2 py-1">Straw</th>
                <th className="px-2 py-1">Tag</th>
                <th className="px-2 py-1">Stage</th>
                <th className="px-2 py-1">Grade</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">DC ID</th>
                <th className="px-2 py-1">Partners</th>
                <th className="px-2 py-1"></th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.embryoId}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-2 py-1">{r.mating ?? ''}</td>
                  <td className="px-2 py-1">{r.ranchCode ?? ''}</td>
                  <td className="px-2 py-1">{r.recipName ?? ''}</td>
                  <td className="px-2 py-1">{r.cane ?? ''}</td>
                  <td className="px-2 py-1">{r.strawNumber ?? ''}</td>
                  <td className="px-2 py-1">{r.tagNumber ?? ''}</td>
                  <td className="px-2 py-1">{r.stage ?? ''}</td>
                  <td className="px-2 py-1">{r.grade ?? ''}</td>
                  <td className="px-2 py-1">{r.status ?? ''}</td>
                  <td className="px-2 py-1">{r.dairyCompId ?? ''}</td>
                  <td className="px-2 py-1">
                    <button
                      type="button"
                      className="text-blue-700 underline hover:text-blue-900"
                      onClick={() =>
                        setPartnershipEmbryo({
                          id: r.embryoId,
                          label: r.mating ?? r.cane ?? r.embryoId,
                        })
                      }
                    >
                      {r.partnerCount ?? 0}
                    </button>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      className="text-xs text-blue-700 underline hover:text-blue-900"
                      onClick={() => setEditEmbryo(r)}
                    >
                      Edit
                    </button>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      className="text-xs text-blue-700 underline hover:text-blue-900 disabled:cursor-not-allowed disabled:text-gray-400"
                      onClick={() => setImplantEmbryo(r)}
                      disabled={!session.transferDate || !session.transferSite}
                      title={
                        !session.transferDate || !session.transferSite
                          ? 'Set transfer date and transfer site in the filter panel first'
                          : undefined
                      }
                    >
                      Implant
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
